import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { SERVICES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", phone: "", service: "", details: "" };

export function QuoteForm({ selectedService, onServiceHandled }) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (selectedService) {
      setForm((f) => ({ ...f, service: selectedService }));
    }
  }, [selectedService]);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service || !form.details) {
      toast.error("Please complete every field.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quote-requests`, form);
      toast.success("Quote received. Dispatch will reply within one business day.");
      setForm(EMPTY);
      if (onServiceHandled) onServiceHandled();
    } catch (err) {
      toast.error("Submission failed. Please email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} id="quote-form" className="border border-[hsl(var(--lh-bone))]/15 bg-[hsl(var(--lh-ink-2))] p-8 md:p-10" data-testid="quote-form">
      <div className="flex items-baseline justify-between mb-8">
        <h3 className="font-display text-3xl">Request a quote.</h3>
        <span className="font-mono text-[0.68rem] uppercase tracking-[0.16em] text-[hsl(var(--lh-bone))]/50">Form · 02</span>
      </div>

      <div className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="lh-label">Name</label>
            <input className="lh-input" value={form.name} onChange={update("name")} data-testid="quote-name"/>
          </div>
          <div>
            <label className="lh-label">Phone</label>
            <input className="lh-input" value={form.phone} onChange={update("phone")} data-testid="quote-phone"/>
          </div>
        </div>
        <div>
          <label className="lh-label">Email</label>
          <input type="email" className="lh-input" value={form.email} onChange={update("email")} data-testid="quote-email"/>
        </div>
        <div>
          <label className="lh-label">Service needed</label>
          <select className="lh-select" value={form.service} onChange={update("service")} data-testid="quote-service">
            <option value="">Select a service…</option>
            {SERVICES.map((s) => <option key={s.id} value={s.formValue}>{s.formValue}</option>)}
            <option value="Other / Not sure">Other / Not sure</option>
          </select>
        </div>
        <div>
          <label className="lh-label">Load / job details</label>
          <textarea rows={5} className="lh-input resize-none" placeholder="Origin, destination, equipment, timing, weight, or shop symptoms…" value={form.details} onChange={update("details")} data-testid="quote-details"/>
        </div>
        <button type="submit" disabled={submitting} className="lh-btn-primary w-full justify-center" data-testid="quote-submit">
          {submitting ? "Sending…" : "Send Request"} <ArrowRight size={14} strokeWidth={2.5}/>
        </button>
      </div>
    </form>
  );
}
